import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
  Paper,
  TextField,
} from "@mui/material";
import { Search } from "lucide-react";
import { Check } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllManifests,
  addLegToManifest,
  createManifest,
  updateOrderStatus,
} from "src/queries/query";
import { OrderStatus } from "src/constants/OrderStatus";
import { toast } from "react-toastify";

export default function AddToExistingManifestModal({
  open,
  handleClose,
  legs = [], // Provide a default empty array for legs
  onAssign, // Keep for potential future use or remove if not needed
  refetchOrders, // Keep for potential future use or remove if not needed
  isOSND = false,
}) {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedManifest, setSelectedManifest] = useState(null);

  const {
    data: manifestListData,
    isLoading: isLoadingManifests,
    error: manifestsError,
  } = useQuery({
    // queryKey: ["allManifests", isOSND],
    // queryFn: () => getAllManifests({ params: isOSND ? '?isOSND=true' : '' }),
    queryKey: ["allManifests"],
    queryFn: () => getAllManifests({ params: "" }),
    enabled: open, // Only fetch when the modal is open
    select: (data) => (Array.isArray(data?.data?.data) ? data?.data?.data : []),
  });

  const manifestList = manifestListData || [];
  const loading = isLoadingManifests; // For backward compatibility with existing loading variable

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedManifest(null);
    }
  }, [open]);

  const filteredManifests = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return manifestList.filter((manifest) => {
      const manifestName =
        manifest?.manifestName || manifest?.manifestNumber || "";
      const manifestNumber =
        typeof manifestName === "string" ? manifestName.toLowerCase() : "";
      const carrierName =
        typeof manifest?.carrier?.name === "string"
          ? manifest.carrier.name.toLowerCase()
          : "";
      return (
        manifestNumber.includes(lowerSearch) ||
        carrierName.includes(lowerSearch)
      );
    });
  }, [manifestList, searchTerm]);

  const addLegMutation = useMutation({
    mutationFn: async ({ manifestUuid, legUuid }) => {
      return addLegToManifest(manifestUuid, legUuid);
    },
    onSuccess: async (data, variables) => {
      toast.success("Leg added to manifest successfully!");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["allManifests"] });
      queryClient.invalidateQueries({
        queryKey: ["manifest", variables.manifestUuid],
      });
      if (refetchOrders) {
        refetchOrders();
      }

      // Update order status to "in-transit" when leg is added to manifest
      const leg = legs.find((l) => l.uuid === variables.legUuid);
      if (leg?.orderStop?.order?.uuid) {
        try {
          await updateOrderStatus(
            leg.orderStop.order.uuid,
            OrderStatus.INTRANSIT,
          );
        } catch (error) {
          console.error("Failed to update order status:", error);
        }
      }
      // Don't call onAssign to avoid duplicate API call
      // handleClose(); // Keep modal open for multiple additions
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to add leg to manifest");
      console.error("Error assigning leg to manifest:", error);
    },
  });

  const createManifestAndAddLegsMutation = useMutation({
    mutationFn: async (legUuids) => {
      const newManifest = await createManifest(legUuids[0], isOSND); // Create manifest with the first leg and isOSND flag
      if (legUuids.length > 1) {
        // Add remaining legs to the new manifest
        for (let i = 1; i < legUuids.length; i++) {
          await addLegToManifest(newManifest.uuid, legUuids[i]);
        }
      }
      return newManifest;
    },
    onSuccess: (data) => {
      toast.success("New manifest created and leg(s) added successfully!");
      queryClient.invalidateQueries({ queryKey: ["allManifests"] });
      queryClient.invalidateQueries({ queryKey: ["legs"] });
      handleClose();
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to create manifest and add leg(s)");
      console.error("Error creating manifest and adding leg(s):", error);
    },
  });

  const handleAssignToManifest = async () => {
    if (!selectedManifest?.uuid) {
      // If no manifest is selected, create a new one and add the selected legs
      if (legs.length === 0) {
        toast.error("No legs selected to create a manifest.");
        return;
      }
      const legUuids = legs.map((l) => l.uuid);
      createManifestAndAddLegsMutation.mutate(legUuids);
    } else {
      // If a manifest is selected, add all selected legs to it
      if (legs.length === 0) {
        toast.error("No legs selected to add to manifest.");
        return;
      }

      let successfulAdditions = 0;
      let failedAdditions = 0;
      const failedLegsMessages = [];

      for (const singleLeg of legs) {
        try {
          await addLegMutation.mutateAsync({
            legUuid: singleLeg.uuid,
            manifestUuid: selectedManifest.uuid,
          });
          successfulAdditions++;
        } catch (error) {
          failedAdditions++;
          const errorMessage =
            error?.message ||
            `Failed to add leg ${singleLeg.legNumber || singleLeg.uuid}`;
          failedLegsMessages.push(
            `Leg ${singleLeg.legNumber || singleLeg.uuid}: ${errorMessage}`,
          );
          // Do not break the loop, continue with other legs
        }
      }

      if (successfulAdditions > 0 && failedAdditions === 0) {
        toast.success(
          `${successfulAdditions} leg(s) added to manifest successfully!`,
        );
        handleClose();
      } else if (successfulAdditions > 0 && failedAdditions > 0) {
        toast.warn(
          `${successfulAdditions} leg(s) added successfully. ${failedAdditions} leg(s) failed: ${failedLegsMessages.join(
            "; ",
          )}`,
        );
        handleClose(); // Close modal if some succeeded
      } else if (failedAdditions === legs.length) {
        // All failed
        toast.error(
          `All ${failedAdditions} leg(s) failed to add to manifest: ${failedLegsMessages.join(
            "; ",
          )}`,
        );
        // Keep modal open for user to review and potentially choose another manifest
      }
    }
  };

  const titleText =
    legs.length > 1 ? "Add Legs to Manifest" : "Add Leg to Manifest";
  const singleLegNumber = legs[0]?.legNumber;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          fontWeight: "bold",
          fontSize: "1.15rem",
        }}
      >
        {titleText}{" "}
        {legs.length === 1 && singleLegNumber ? `: ${singleLegNumber}` : ""}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, pb: 0 }}>
        <Box sx={{ mx: "auto", p: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for manifests by manifest number or carrier name.
          </Typography>

          <TextField
            type="text"
            variant="outlined"
            placeholder="Search manifests..."
            fullWidth
            sx={{ mb: 1, borderRadius: "8px" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            endAdornment={
              <InputAdornment position="start">
                <Search className="h-4 w-4 text-gray-400 mr-2" />
              </InputAdornment>
            }
          />

          <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
            {searchTerm ? "Search results" : "Available Manifests"}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ maxHeight: "300px", overflowY: "auto", py: 1 }}>
              {filteredManifests.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {filteredManifests.map((manifest) => (
                    <Paper
                      key={manifest.uuid}
                      sx={{
                        p: 0,
                        px: 1,
                        border: `1px solid ${
                          selectedManifest?.uuid === manifest.uuid
                            ? "#1e55bf"
                            : "#e0e0e0"
                        }`,
                        mx: 0.5,
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexDirection: "row",
                          cursor: "pointer",
                          width: "100%",
                        }}
                        onClick={() => {
                          if (selectedManifest?.uuid === manifest.uuid) {
                            setSelectedManifest(null);
                          } else {
                            setSelectedManifest(manifest);
                          }
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexDirection: "row",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "start",
                              flexDirection: "column",
                              gap: 0.3,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "bold", color: "text.primary" }}
                            >
                              Manifest:{" "}
                              {manifest?.manifestName ||
                                manifest?.manifestNumber ||
                                "N/A"}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Driver:{" "}
                                {manifest?.driver?.firstName
                                  ? manifest?.driver?.firstName +
                                    " " +
                                    manifest?.driver?.lastName
                                  : "N/A"}
                              </Typography>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Carrier: {manifest?.carrier?.name || "N/A"}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.disabled">
                            Status: {manifest?.status || "N/A"} | Legs:{" "}
                            {manifest?.legs?.length ?? 0}
                          </Typography>
                        </Box>
                        {selectedManifest?.uuid === manifest.uuid && (
                          <Check sx={{ color: "primary.main" }} />
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                >
                  <Typography variant="body2">
                    {searchTerm
                      ? "No manifests match your search criteria."
                      : "No manifests available."}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 1,
          borderTop: "1px solid #e0e0e0",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleClose}
          color="error"
          variant="outlined"
          sx={{ mr: 1, minWidth: "100px" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssignToManifest}
          variant="contained"
          color="primary"
          disabled={addLegMutation.isPending}
          sx={{ minWidth: "140px" }}
        >
          {addLegMutation.isPending ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <CircularProgress size={24} color="inherit" />
            </Box>
          ) : selectedManifest ? (
            "Add to Manifest"
          ) : (
            "Create New Manifest and Add Leg(s)"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
